import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import { Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from "chart.js";
import { useFinance } from "../contexts/FinanceContext";
import { format, isAfter, subDays } from "date-fns";
import { useCurrency } from "../utils/useCurrency";

// 1. Import useTranslation hook
import { useTranslation } from "react-i18next";

import {
  FaPiggyBank,
  FaMoneyBillWave,
  FaStar,
  FaEdit,
  FaTrashAlt,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
// icons
import {
  FaUtensils,
  FaHome,
  FaCar,
  FaTshirt,
  FaBook,
  FaGift,
  FaPlane,
  FaHeartbeat,
  FaGem,
} from "react-icons/fa";
import { IoGameController } from "react-icons/io5";
import BestDayWorstDay from "../components/BestDayWorstDay";
import ExpenseLimit from "../components/ExpenseLimit";
import SpendingsFinanceComp from "../components/SpendingsFinanceComp";
import Navbar from "../components/Navbar";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
);

const categoryIconMap = {
  "Food / Drinks": FaUtensils,
  "Home / Rent": FaHome,
  "Car & Fuel": FaCar,
  Clothes: FaTshirt,
  Entertainment: IoGameController,
  Education: FaBook,
  Gifts: FaGift,
  Travel: FaPlane,
  Health: FaHeartbeat,
  Beauty: FaGem,
};

export default function Finances({ theme, switchTheme }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    document.title = "Finances - ASites Planner";
  }, []);
  // 2. Use the hook instead of the direct instance
  // This hook ensures the page re-renders instantly when language changes
  const { t } = useTranslation();

  const {
    history = [],
    categories = [],
    loading,
    deleteTransaction,
    editTransaction,
  } = useFinance();
  const { formatCurrency } = useCurrency();
  const [query, setQuery] = useState("");
  const [timeRange, setTimeRange] = useState("all");

  const [editing, setEditing] = useState(null);

  const startEdit = (transaction) => {
    setEditing({
      id: transaction.id,
      type: transaction.type,
      amount: String(transaction.amount ?? ""),
      category: transaction.category ?? "",
      note: transaction.note ?? "",
    });
  };

  const cancelEdit = () => setEditing(null);

  const saveEdit = async () => {
    if (!editing) return;

    const amountNum = Number(editing.amount);
    if (!Number.isFinite(amountNum)) return;

    const updatedData = {
      amount: amountNum,
      note: editing.note || "",
    };

    if (editing.type !== "income") {
      updatedData.category = editing.category || "-";
    }

    try {
      await editTransaction(editing.id, updatedData);
      setEditing(null);
    } catch {
      // non-fatal: FinanceContext will surface issues via Firestore failures
    }
  };

  const handleDelete = async (transactionId) => {
    if (!transactionId) return;
    const ok = window.confirm("Delete this transaction?");
    if (!ok) return;
    try {
      await deleteTransaction(transactionId);
      if (editing?.id === transactionId) setEditing(null);
    } catch {
      // non-fatal
    }
  };

  // Filter transactions based on time range
  const filteredHistory = useMemo(() => {
    if (!history || history.length === 0) return [];

    if (timeRange === "all") return history;

    const now = new Date();
    let cutoffDate;

    switch (timeRange) {
      case "7days":
        cutoffDate = subDays(now, 7);
        break;
      case "30days":
        cutoffDate = subDays(now, 30);
        break;
      case "90days":
        cutoffDate = subDays(now, 90);
        break;
      default:
        return history;
    }

    return history.filter((transaction) => {
      const transactionDate = new Date(transaction.date);
      return isAfter(transactionDate, cutoffDate);
    });
  }, [history, timeRange]);

  // transactions sorted by date desc (using filteredHistory)
  const transactions = useMemo(() => {
    const copy = (filteredHistory || []).slice();
    copy.sort((a, b) => new Date(b.date) - new Date(a.date));
    return copy;
  }, [filteredHistory]);

  // totals (using filteredHistory)
  const totals = useMemo(() => {
    let income = 0;
    let spending = 0;
    filteredHistory.forEach((h) => {
      const amt = Math.abs(Number(h.amount || 0));
      if (h.type === "income") income += amt;
      if (h.type === "spending") spending += amt;
    });
    return { income, spending, balance: income - spending };
  }, [filteredHistory]);
  const isDark = localStorage.getItem("theme") === "dark";

  // category totals (using filteredHistory)
  const categoryTotals = useMemo(() => {
    const map = {};
    filteredHistory.forEach((h) => {
      if (h.type !== "spending") return;
      const name = h.category || "-";
      map[name] = (map[name] || 0) + Math.abs(Number(h.amount || 0));
    });
    return Object.entries(map)
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount);
  }, [filteredHistory]);

  const lineChartData = useMemo(() => {
    if (!filteredHistory || filteredHistory.length === 0) return null;

    const labels = filteredHistory.map((h) =>
      format(new Date(h.date), "MMM dd")
    );
    const values = filteredHistory.map((h) =>
      h.type === "income"
        ? Math.abs(Number(h.amount || 0))
        : -Math.abs(Number(h.amount || 0))
    );

    let runningBalance = 0;
    const running = values.map((v) => (runningBalance += v));

    return {
      labels,
      datasets: [
        {
          label: t("finances.balance"),
          data: running,
          borderColor: isDark ? "#38bdf8" : "#0ea5e9",
          backgroundColor: "rgba(56, 189, 248, 0.12)",
          borderWidth: 2,
          tension: 0.35,
          fill: true,
        },
      ],
    };
  }, [filteredHistory, isDark, t]);

  const gridColor = theme === "dark" ? "#363d52" : "#ddd";

  // pie chart data (using categoryTotals from filteredHistory)
  const pieData = useMemo(() => {
    const labels = categoryTotals.map((c) => c.name);
    const data = categoryTotals.map((c) => Math.round(c.amount * 100) / 100);
    const baseColors = [
      "#4f46e5",
      "#0ea5a3",
      "#f59e0b",
      "#ef4444",
      "#06b6d4",
      "#8b5cf6",
      "#10b981",
      "#f97316",
      "#a78bfa",
      "#ef9a9a",
    ];
    const backgroundColor = labels.map(
      (_, i) => baseColors[i % baseColors.length]
    );
    return { labels, datasets: [{ data, backgroundColor, hoverOffset: 8 }] };
  }, [categoryTotals]);

  const pieOptions = {
    plugins: {
      legend: { position: "right", labels: { boxWidth: 12, padding: 12 } },
    },
    maintainAspectRatio: false,
  };

  const topCategory = categoryTotals[0] || null;
  const totalSpent = totals.spending;

  const filteredTransactions = transactions.filter((t) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (t.category || "").toLowerCase().includes(q) ||
      (t.note || "").toLowerCase().includes(q) ||
      String(t.amount).toLowerCase().includes(q)
    );
  });

  const savingsRate = totals.income
    ? Math.round(((totals.income - totals.spending) / totals.income) * 100)
    : 0;

  const isMobileView = window.innerWidth <= 650;

  return (
    <PageContainer>
      <Navbar theme={theme} switchTheme={switchTheme} />
      <Header>
        <Title>{t("finances.title")}</Title>
        <Subtitle>{t("finances.subtitle")}</Subtitle>

        {/* Time Range Selector */}
        <TimeRangeSelector>
          <TimeRangeLabel>{t("finances.view")}:</TimeRangeLabel>
          <TimeRangeButtons>
            <TimeRangeButton
              active={timeRange === "all"}
              onClick={() => setTimeRange("all")}
            >
              {t("finances.allTime")}
            </TimeRangeButton>
            <TimeRangeButton
              active={timeRange === "90days"}
              onClick={() => setTimeRange("90days")}
            >
              {t("finances.last90Days")}
            </TimeRangeButton>
            <TimeRangeButton
              active={timeRange === "30days"}
              onClick={() => setTimeRange("30days")}
            >
              {t("finances.last30Days")}
            </TimeRangeButton>
            <TimeRangeButton
              active={timeRange === "7days"}
              onClick={() => setTimeRange("7days")}
            >
              {t("finances.last7Days")}
            </TimeRangeButton>
          </TimeRangeButtons>
        </TimeRangeSelector>
      </Header>

      <Insights className="insights" style={{ transition: "0.5s " }}>
        <InsightsGrid style={{ transition: "0.5s" }}>
          <InsightBox style={{ transition: "0.5s " }}>
            <InsightLabel>
              <FaMoneyBillWave style={{ marginRight: 6 }} />{" "}
              {t("finances.totalIncome")}
            </InsightLabel>
            <InsightValue color="#10b981">
              {formatCurrency(totals.income)}
            </InsightValue>
            <InsightSub>
              {filteredHistory.length
                ? `${t("finances.avgPerTransaction")}: ${formatCurrency(
                    totals.income / filteredHistory.length
                  )}`
                : "—"}
            </InsightSub>
          </InsightBox>

          <InsightBox>
            <InsightLabel>
              <FaMoneyBillWave style={{ marginRight: 6 }} />{" "}
              {t("finances.totalSpent")}
            </InsightLabel>
            <InsightValue color="#ef4444">
              {formatCurrency(totalSpent)}
            </InsightValue>
            <InsightSub>
              {t("finances.top")}:{" "}
              {topCategory
                ? `${topCategory.name} • ${formatCurrency(topCategory.amount)}`
                : "—"}
            </InsightSub>
          </InsightBox>

          <InsightBox>
            <InsightLabel>
              <FaStar style={{ marginRight: 6 }} /> {t("finances.topCategory")}
            </InsightLabel>
            <InsightValue color="#38bdf8">
              {topCategory
                ? `${topCategory.name} • ${Math.round(
                    (topCategory.amount / (totalSpent || 1)) * 100
                  )}%`
                : "—"}
            </InsightValue>
            <InsightSub>
              {topCategory
                ? `${formatCurrency(topCategory.amount)} ${t("finances.spent")}`
                : "—"}
            </InsightSub>
          </InsightBox>

          <InsightBox>
            <InsightLabel>
              <FaPiggyBank style={{ marginRight: 6 }} />{" "}
              {t("finances.savingsRate")}
            </InsightLabel>
            <InsightValue color={savingsRate >= 50 ? "#10b981" : "#ef4444"}>
              {savingsRate}%
            </InsightValue>
            <InsightSub>
              {formatCurrency(totals.income - totals.spending)}{" "}
              {t("finances.saved")}
            </InsightSub>
          </InsightBox>
        </InsightsGrid>
      </Insights>

      <Transactions>
        <SectionCard className="transactionsCard">
          <SectionTitle>{t("finances.transactions")}</SectionTitle>
          <TransactionsHeader>
            <div style={{ maxWidth: 140 }}>{t("finances.date")}</div>
            <div>{t("finances.category")}</div>
            <div>{t("finances.note")}</div>
            <div style={{ textAlign: "right" }}>{t("finances.amount")}</div>
            <div style={{ textAlign: "right" }}> </div>
          </TransactionsHeader>
          <TransactionsList>
            {loading ? (
              <EmptyRow>{t("finances.loading")}</EmptyRow>
            ) : filteredTransactions.length === 0 ? (
              <EmptyRow>{t("finances.noTransactions")}</EmptyRow>
            ) : (
              // 3. IMPORTANT: We keep the variable name 'transaction' here
              // so it doesn't conflict with 't()' from useTranslation
              filteredTransactions.map((transaction, i) => {
                const Icon = categoryIconMap[transaction.category] || FaGem;
                const isEditing = editing?.id === transaction.id;
                return (
                  <TransactionRow key={transaction.id || i}>
                    {/* 1. DATE CONTAINER (Now includes Amount on mobile) */}
                    <div
                      style={{
                        minWidth: 100,
                        maxWidth: isMobileView ? "100%" : "140px",
                      }}
                    >
                      {/* This is the Date block */}
                      <span>
                        <MobileLabel>{t("finances.date")}:</MobileLabel>
                        {format(new Date(transaction.date), "yyyy-MM-dd HH:mm")}
                      </span>

                      {/* This is the Amount block */}
                      {isMobileView && (
                        <div style={{ paddingLeft: "8px" }}>
                          {isEditing ? (
                            <InlineInput
                              type="number"
                              value={editing.amount}
                              onChange={(e) =>
                                setEditing((prev) =>
                                  prev
                                    ? { ...prev, amount: e.target.value }
                                    : prev
                                )
                              }
                              style={{ width: 120 }}
                            />
                          ) : transaction.type === "income" ? (
                            <AmountPositive>
                              +{formatCurrency(transaction.amount)}
                            </AmountPositive>
                          ) : (
                            <AmountNegative>
                              -{formatCurrency(transaction.amount)}
                            </AmountNegative>
                          )}
                        </div>
                      )}
                    </div>

                    {/* 2. CATEGORY CONTAINER */}
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <MobileLabel>{t("finances.category")}:</MobileLabel>
                      <Icon />
                      {isEditing && transaction.type !== "income" ? (
                        <InlineSelect
                          value={editing.category}
                          onChange={(e) =>
                            setEditing((prev) =>
                              prev
                                ? { ...prev, category: e.target.value }
                                : prev
                            )
                          }
                        >
                          {categories.length === 0 ? (
                            <option value={editing.category || "-"}>
                              {editing.category || "-"}
                            </option>
                          ) : (
                            categories.map((c) => (
                              <option key={c.id} value={c.name}>
                                {c.name}
                              </option>
                            ))
                          )}
                        </InlineSelect>
                      ) : (
                        <span>{transaction.category || "-"}</span>
                      )}
                    </div>

                    {/* 3. NOTE CONTAINER */}
                    <div style={{ color: "#9aa4c0" }}>
                      <MobileLabel>{t("finances.note")}:</MobileLabel>
                      {isEditing ? (
                        <InlineInput
                          value={editing.note}
                          onChange={(e) =>
                            setEditing((prev) =>
                              prev ? { ...prev, note: e.target.value } : prev
                            )
                          }
                          placeholder={transaction.note || ""}
                        />
                      ) : (
                        transaction.note || "-"
                      )}
                    </div>

                    {/* 4. AMOUNT CONTAINER (Original position - only visible on desktop) */}
                    <div style={{ textAlign: "right", minWidth: 110 }}>
                      {!isMobileView &&
                        (isEditing ? (
                          <InlineInput
                            type="number"
                            value={editing.amount}
                            onChange={(e) =>
                              setEditing((prev) =>
                                prev
                                  ? { ...prev, amount: e.target.value }
                                  : prev
                              )
                            }
                            style={{ width: 120, textAlign: "right" }}
                          />
                        ) : transaction.type === "income" ? (
                          <AmountPositive>
                            +{formatCurrency(transaction.amount)}
                          </AmountPositive>
                        ) : (
                          <AmountNegative>
                            -{formatCurrency(transaction.amount)}
                          </AmountNegative>
                        ))}
                    </div>

                    {/* 5. ACTIONS (Edit / Delete or Save / Cancel) */}
                    <ActionsCell>
                      {isEditing ? (
                        <>
                          <IconActionButton
                            onClick={saveEdit}
                            title="Save"
                            aria-label="Save"
                          >
                            <FaCheck />
                          </IconActionButton>
                          <IconActionButton
                            onClick={cancelEdit}
                            title="Cancel"
                            aria-label="Cancel"
                          >
                            <FaTimes />
                          </IconActionButton>
                        </>
                      ) : (
                        <>
                          <IconActionButton
                            onClick={() => startEdit(transaction)}
                            title="Edit"
                            aria-label="Edit"
                          >
                            <FaEdit />
                          </IconActionButton>
                          <IconActionButton
                            onClick={() => handleDelete(transaction.id)}
                            title="Delete"
                            aria-label="Delete"
                            danger
                          >
                            <FaTrashAlt />
                          </IconActionButton>
                        </>
                      )}
                    </ActionsCell>
                  </TransactionRow>
                );
              })
            )}
          </TransactionsList>
        </SectionCard>
        <SpendingsFinanceComp className="balance" theme={theme} />
      </Transactions>

      <BottomGrid>
        <SectionCard className="btgCard1">
          <SectionTitle>{t("finances.whereMoneyWent")}</SectionTitle>

          {categoryTotals.length === 0 ? (
            <EmptyRow style={{ height: 220 }}>
              {t("finances.noSpending")}
            </EmptyRow>
          ) : (
            <>
              <TotalSpent>
                {t("finances.totalSpent")}:{" "}
                <span>{formatCurrency(totalSpent)}</span>
              </TotalSpent>
              <PieContainer>
                <Pie data={pieData} options={pieOptions} />
                <Legenda>
                  {categoryTotals.map((c, i) => {
                    const color = pieData.datasets[0].backgroundColor[i];
                    return (
                      <LegendItem key={c.name}>
                        <ColorDot
                          style={{
                            backgroundColor: color,
                          }}
                        />
                        <LegendLabel>
                          {c.name}: {formatCurrency(c.amount)} (
                          {((c.amount / totalSpent) * 100).toFixed(0)}%)
                        </LegendLabel>
                      </LegendItem>
                    );
                  })}
                </Legenda>
              </PieContainer>
            </>
          )}
        </SectionCard>

        <LimitAndMax className="btgCard2">
          <ExpenseLimit history={history} />
          <BestDayWorstDay />
        </LimitAndMax>

        <SectionCard className="btgCard3">
          <SectionTitle>{t("finances.categoryBreakdown")}</SectionTitle>
          <CategoryList>
            {categoryTotals.length === 0 && (
              <EmptyRow>{t("finances.noCategories")}</EmptyRow>
            )}
            {categoryTotals.map((c) => {
              const percent = totalSpent
                ? ((c.amount / totalSpent) * 100).toFixed(0)
                : "0";
              const Icon = categoryIconMap[c.name] || FaGem;
              return (
                <CategoryRow key={c.name}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <Icon />
                    <div>
                      <div
                        style={{
                          fontWeight: 500,
                          color: "var(--primaryColor)",
                        }}
                      >
                        {c.name}
                      </div>
                      <div style={{ color: "#8f9bb3", fontSize: 13 }}>
                        {formatCurrency(c.amount)}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>{percent}%</div>
                </CategoryRow>
              );
            })}
          </CategoryList>
        </SectionCard>
      </BottomGrid>
    </PageContainer>
  );
}

/* ---------------- Styled Components ---------------- */

const PageContainer = styled.div`
  padding: 24px 0 24px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: 0.5s;
  margin: 0;
  @media (max-width: 1200px) {
    gap: 1px;
  }
  @media (max-width: 650px) {
    margin-top: 120px;
  }
`;

const Header = styled.header`
  margin-bottom: 0.5rem;
  margin-left: 5%;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: linear-gradient(90deg, var(--primaryColor), #8b5cf6);
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: var(--fontSecondaryColor);
  font-size: 1rem;
`;

// Time Range Selector Styles
const TimeRangeSelector = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;
  gap: 12px;
  flex-wrap: wrap;
`;

const TimeRangeLabel = styled.span`
  font-size: 14px;
  color: var(--fontSecondaryColor);
  font-weight: 500;
`;

const TimeRangeButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const TimeRangeButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  background: ${(props) =>
    props.active ? "var(--primaryColor)" : "var(--secondaryBackgroundColor)"};
  color: ${(props) => (props.active ? "white" : "var(--fontSecondaryColor)")};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.active ? "var(--primaryColor)" : "rgba(255, 255, 255, 0.05)"};
  }

  @media (max-width: 650px) {
    padding: 5px 10px;
    font-size: 12px;
  }
`;

const MobileLabel = styled.span`
  display: none;
  font-weight: 500;
  color: var(--fontSecondaryColor);
  margin-right: 8px;

  @media (max-width: 650px) {
    display: inline;
  }
`;

const TotalSpent = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 16px;
  text-align: center;
  transition: 0.5;
  span {
    color: #ef4444;
    font-weight: 600;
    margin-left: 6px;
  }
`;

const PieContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
  margin-top: 5em;
  transition: 0.5s;
  @media (max-width: 1100px) {
    flex-direction: column;
    margin-top: 0;
    max-height: 600px;
  }
`;

const Legenda = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
  transition: 0.5s;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  transition: 0.5s;
  gap: 10px;
`;

const LimitAndMax = styled.div`
  display: flex;
  gap: 2em;
  flex-direction: column;
`;

const ColorDot = styled.div`
  width: 18px;
  height: 14px;
  display: none;
  border-radius: 50%;
  border: 2px solid #df0f0f;
`;

const LegendLabel = styled.div`
  font-size: 14px;
  color: var(--fontPrimaryColor, #f81515);
`;

const SectionCard = styled.div`
  background: var(--secondaryBackgroundColor, #0f1724);
  padding: 3.2em;
  border-radius: 14px;
  color: var(--fontPrimaryColor, #fff);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: 0.5s;
  .insights {
    background-color: red;
  }
`;

const Transactions = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: 2em;
  transition: 0.5s;

  .transactionsCard {
    width: 57%;
    @media (max-width: 650px) {
      padding: 40px 20px;
    }
  }
  .balance {
    background: var(--secondaryBackgroundColor, #0f1724);
    padding: 3.2em;
    border-radius: 14px;
    color: var(--fontPrimaryColor, #fff);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
    transition: 0.5s;
    width: 43%;
    display: none;
  }
  @media (max-width: 1650px) {
    .transactionsCard {
      width: 100%;
    }
  }
  @media (max-width: 1200px) {
    gap: 1em;
  }
`;

const Balance = styled.div`
  background: var(--secondaryBackgroundColor, #0f1724);
  padding: 1.2em;
  border-radius: 14px;
  color: var(--fontPrimaryColor, #fff);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: 0.5s;
  width: 40%;
  .balance {
    height: auto;
    width: 100%;
  }
`;

const Insights = styled.div`
  padding: 16px;
  border-radius: 14px;
  color: var(--fontPrimaryColor, #fff);
  transition: 0.5s;
  @media (max-width: 520px) {
    padding: 16px 0;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 12px 0;
  font-weight: 600;
  text-align: center;
  transition: 0.5s;
`;

const InsightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2em;
  transition: 0.5s;
  @media (max-width: 1200px) {
    gap: 1em;
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 430px) {
    gap: 10px;
  }
`;

const InsightBox = styled.div`
  padding: 28px 30px;
  display: flex;
  transition: 0.5s;
  flex-direction: column;
  gap: 1em;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  transition: 0.5s;
  background: var(--secondaryBackgroundColor);
  @media (max-width: 430px) {
    padding: 20px;
  }
`;

const InsightLabel = styled.div`
  font-size: 15px;
  color: var(--fontSecondaryColor);
  @media (max-width: 430px) {
    font-size: 13px;
  }
`;

const InsightValue = styled.div`
  font-weight: 500;
  margin-top: 6px;
  margin-left: 10px;
  font-size: 28px;
  color: ${(props) => props.color || "inherit"};
  transition: 0.5s;
  @media (max-width: 1060px) {
    font-size: 24px;
  }
  @media (max-width: 430px) {
    font-size: 20px;
  }
`;

const InsightSub = styled.div`
  font-size: 14px;
  color: var(--fontSecondaryColor);
  margin-top: 15px;
  transition: 0.5s;
  @media (max-width: 430px) {
    font-size: 13px;
  }
`;

const TransactionsHeader = styled.div`
  display: grid;
  grid-template-columns: 20% 25% 1fr 16% 8%;
  gap: 12px;
  color: #9aa4c0;
  font-size: 13px;
  padding: 8px 4px;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.03);
  transition: 0.5s;
  width: 55%;
  width: 100%;
  @media (max-width: 650px) {
    display: none;
  }
`;

const TransactionsList = styled.div`
  max-height: 420px;
  overflow: auto;
  margin-top: 12px;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #717d9b;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background-color: var(--secondaryBackgroundColor);
  }
`;

const TransactionRow = styled.div`
  display: grid;
  grid-template-columns: 20% 25% 1fr 16% 8%;
  gap: 1em;
  align-items: center;
  padding: 10px 6px;
  border-radius: 10px;
  margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.02);

  @media (max-width: 650px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 12px 18px;
    margin: 10px 2px;
    gap: 10px;
    box-shadow: 0 0 12px 1px var(--primaryBackgroundColor);

    & > div:first-child {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    & > div:nth-child(2),
    & > div:nth-child(3) {
      width: 100%;
    }

    & > div:nth-child(4) {
      display: none;
    }

    & > div:last-child {
      width: 100%;
      display: flex;
      justify-content: flex-end;
      padding-top: 6px;
    }
  }
`;

const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
`;

const IconActionButton = styled.button`
  background: transparent;
  border: none;
  color: ${(p) =>
    p.danger ? "rgba(239, 68, 68, 0.9)" : "var(--fontSecondaryColor)"};
  cursor: pointer;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: ${(p) => (p.danger ? "#ef4444" : "var(--primaryColor)")};
  }
`;

const InlineInput = styled.input`
  background: var(--thirdBackgroundColor);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: var(--fontPrimaryColor);
  padding: 6px 10px;
  border-radius: 8px;
  outline: none;
  width: 100%;

  &:focus {
    border-color: rgba(56, 189, 248, 0.5);
  }
`;

const InlineSelect = styled.select`
  background: var(--thirdBackgroundColor);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: var(--fontPrimaryColor);
  padding: 6px 10px;
  border-radius: 8px;
  outline: none;

  &:focus {
    border-color: rgba(56, 189, 248, 0.5);
  }
`;

const AmountPositive = styled.span`
  color: #10b981;
  font-weight: 500;
`;

const AmountNegative = styled.span`
  color: #ef4444;
  font-weight: 500;
`;

const BottomGrid = styled.div`
  display: flex;
  gap: 2em;
  margin-top: 1em;
  .btgCard1 {
    width: 32%;
  }
  .btgCard2 {
    width: 32%;
  }
  .btgCard3 {
    width: 32%;
  }

  @media (max-width: 1700px) {
    flex-wrap: wrap;
    .btgCard1 {
      width: 54%;
    }
    .btgCard2 {
      width: 42%;
    }
    .btgCard3 {
      width: 100%;
    }
  }
  @media (max-width: 1030px) {
    flex-wrap: wrap;
    .btgCard1 {
      width: 100%;
    }
    .btgCard2 {
      width: 48%;
    }
    .btgCard3 {
      width: 49%;
    }
  }
  @media (max-width: 1200px) {
    gap: 1em;
  }
  @media (max-width: 800px) {
    .btgCard2 {
      width: 100%;
    }
    .btgCard3 {
      width: 100%;
      padding: 30px;
    }
  }
`;

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 3em;
`;

const CategoryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  border-radius: 10px;
  background: var(--thirdBackgroundColor);
  transition: 0.5s;
`;

const PieWrapper = styled.div`
  display: flex;
  height: stretch;
  flex-direction: column-reverse;
  align-items: center;
  justify-content: center;
`;

const EmptyRow = styled.div`
  padding: 24px;
  color: #9aa4c0;
  text-align: center;
`;
